import {
    DetailedHTMLProps,
    FC,
    ForwardedRef,
    HTMLAttributes,
    forwardRef,
    useCallback,
    useImperativeHandle,
    useRef,
} from 'react';
import styles from './ShadowScrollContainer.module.scss';

const getDefaultBackground = () => {
    const div = document.createElement('div');
    document.head.appendChild(div);

    const bg = window.getComputedStyle(div).backgroundColor;

    document.head.removeChild(div);

    return bg;
};

const getInheritedBackgroundColor = (element: HTMLElement): string => {
    // get default style for current browser
    const defaultStyle = getDefaultBackground(); // typically "rgba(0, 0, 0, 0)"

    // get computed color for el
    const backgroundColor = window.getComputedStyle(element).backgroundColor;

    // if we got a real value, return it
    if (backgroundColor !== defaultStyle) return backgroundColor;

    // if we've reached the top parent el without getting an explicit color, return default
    if (!element.parentElement) return defaultStyle;

    // otherwise, recursively try again on parent element
    return getInheritedBackgroundColor(element.parentElement);
};

interface ShadowScrollContainerProps extends DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement> {
    maxHeight?: string | number;
    ref?: ForwardedRef<HTMLDivElement>;
}

const ShadowScrollContainer = forwardRef<HTMLDivElement, ShadowScrollContainerProps>((props, ref) => {
    const { children, maxHeight = 'inherit', className, style = {}, ...rest } = props;

    const internalRef = useRef<HTMLDivElement>(null);
    const internalRefSetter = useCallback((node: HTMLDivElement) => {
        internalRef.current = node;

        if (node) {
            const bgColor = getInheritedBackgroundColor(node);

            node.style.setProperty('--shadow-scroll-background-color', bgColor);
        }
    }, []);

    useImperativeHandle(ref, () => internalRef.current, [internalRef]);

    return (
        <div
            ref={internalRefSetter}
            className={['overflow-y-auto', styles['shadow-scroll'], className].join(' ')}
            style={{ ...style, maxHeight }}
            {...rest}
        >
            {children}
        </div>
    );
});

export default ShadowScrollContainer;
